import { apiClient, type PageData } from './client'
import type { PromotionBundle } from './promotion-bundles'

// ============ Types ============

export interface CrmebUser {
  uid: number
  nickname: string
  avatar: string
  phone: string
  now_money: number
  integral: number
  real_name: string
  status: number
}

export interface StoreProduct {
  id: number
  store_name: string
  image: string
  slider_image: string
  price: number
  vip_price: number
  stock: number
  sales: number
  cate_id: string
}

export interface ProductAttrValue {
  id: number
  product_id: number
  suk: string
  price: number
  cost: number
  stock: number
  image: string
  unique: string
  vip_price: number
}

export interface ProductDetail extends StoreProduct {
  attr_value: ProductAttrValue[]
}

export interface UserAddress {
  id: number
  uid: number
  real_name: string
  phone: string
  province: string
  city: string
  district: string
  street: string
  detail: string
  is_default: number
}

export interface CartItem {
  productId: number
  productName: string
  productImage: string
  cartNum: number
  price: number
  unique: string
  suk: string
}

export interface SelectedPromotionBundle {
  bundleId: number
  bundleName: string
  quantity: number
  originalPrice: number
  promotionPrice: number
  items: PromotionBundle['items']
}

export type PaymentQrcodeStatus = 'available' | 'no_department' | 'no_qrcode'

export interface CreateOrderResult {
  order_id: string
  pay_price: number
  payment_qrcode_status: PaymentQrcodeStatus
  payment_qrcode: string
  payment_department_id: number
  payment_department_name: string
}

// ============ API ============

export const adminOrderApi = {
  /**
   * 搜索 CRMEB 用户
   */
  searchUsers: async (keyword: string, page = 1, pageSize = 30): Promise<PageData<CrmebUser>> => {
    const params = new URLSearchParams({ keyword, page: String(page), page_size: String(pageSize) })
    const response = await apiClient.get<PageData<CrmebUser>>(`/admin/orders/users?${params}`)
    return response.data
  },

  /**
   * 获取 CRMEB 用户
   */
  getUser: async (uid: number): Promise<CrmebUser> => {
    const response = await apiClient.get<CrmebUser>(`/admin/orders/users/${uid}`)
    return response.data
  },

  /**
   * 搜索商品列表
   */
  listProducts: async (keyword: string, page = 1, pageSize = 30): Promise<PageData<StoreProduct>> => {
    const params = new URLSearchParams({ keyword, page: String(page), page_size: String(pageSize) })
    const response = await apiClient.get<PageData<StoreProduct>>(`/admin/orders/products?${params}`)
    return response.data
  },

  /**
   * 获取商品详情（含 SKU）
   */
  getProductDetail: async (id: number): Promise<ProductDetail> => {
    const response = await apiClient.get<ProductDetail>(`/admin/orders/products/${id}`)
    return response.data
  },

  /**
   * 获取用户地址列表
   */
  listAddresses: async (uid: number): Promise<UserAddress[]> => {
    const response = await apiClient.get<UserAddress[]>(`/admin/orders/addresses/${uid}`)
    return response.data
  },

  /**
   * 创建订单（直接写 DB，一步完成）
   */
  create: async (params: {
    uid: number
    items: Array<{ product_id: number; sku_unique: string; quantity: number }>
    bundle_items?: Array<{ bundle_id: number; quantity: number }>
    address_id: number
    real_name?: string
    phone?: string
    address?: string
    province?: string
    city?: string
    district?: string
    city_id?: number
    detail?: string
    save_as_common_address?: boolean
    shipping_type: number
    postage: number
    mark: string
    is_store?: boolean
    is_cash?: boolean
    shipping_partial?: boolean
    shipping_remark?: string
  }): Promise<CreateOrderResult> => {
    const response = await apiClient.post<CreateOrderResult>(
      '/admin/orders/create',
      params,
    )
    return response.data
  },
}
